import type {
  ClinicianWorkbenchPatientRecordFieldMeta,
  ClinicianWorkbenchPatientRecordFieldPath,
  ClinicianWorkbenchPatientRecordV1,
  ClinicianWorkbenchUpsertPatientRecordActionInput,
} from "@amplecare/contracts";
import { createEmptyClinicianWorkbenchPatientRecord } from "@amplecare/contracts";

export function createEmptyPatientRecord(): ClinicianWorkbenchPatientRecordV1 {
  return createEmptyClinicianWorkbenchPatientRecord();
}

export function getPatientRecordValueByPath(
  root: unknown,
  fieldPath: ClinicianWorkbenchPatientRecordFieldPath,
): unknown {
  return fieldPath.split(".").reduce<unknown>((cursor, segment) => {
    if (cursor == null || typeof cursor !== "object") {
      return undefined;
    }

    return (cursor as Record<string, unknown>)[segment];
  }, root);
}

export function setPatientRecordValueByPath(
  root: Record<string, unknown>,
  fieldPath: ClinicianWorkbenchPatientRecordFieldPath,
  value: unknown,
): void {
  const segments = fieldPath.split(".");
  const leaf = segments.pop();

  if (!leaf) {
    return;
  }

  let cursor: Record<string, unknown> = root;

  for (const segment of segments) {
    const next = cursor[segment];

    if (next == null || typeof next !== "object") {
      cursor[segment] = {};
    }

    cursor = cursor[segment] as Record<string, unknown>;
  }

  cursor[leaf] = value;
}

export interface PatientRecordUpsertContext {
  updatedBy: string;
  appliedAt: string;
  source?: ClinicianWorkbenchPatientRecordFieldMeta["source"];
  /** e.g. "suggested" for call-ingested answers awaiting nurse confirmation. */
  reviewState?: ClinicianWorkbenchPatientRecordFieldMeta["reviewState"];
}

export function applyPatientRecordUpsert(
  existing: ClinicianWorkbenchPatientRecordV1 | null | undefined,
  action: Pick<
    ClinicianWorkbenchUpsertPatientRecordActionInput,
    "record" | "touchedFieldPaths" | "reviewConfirmations"
  >,
  context: PatientRecordUpsertContext,
): ClinicianWorkbenchPatientRecordV1 {
  const record = structuredClone(existing ?? createEmptyPatientRecord());

  for (const fieldPath of action.touchedFieldPaths) {
    const value = getPatientRecordValueByPath(action.record, fieldPath);

    if (value === undefined) {
      continue;
    }

    setPatientRecordValueByPath(
      record as unknown as Record<string, unknown>,
      fieldPath,
      structuredClone(value),
    );
    record.fieldMeta[fieldPath] = {
      source: context.source ?? "manual",
      ...(context.reviewState ? { reviewState: context.reviewState } : {}),
      updatedAt: context.appliedAt,
      updatedBy: context.updatedBy,
    };
  }

  for (const fieldPath of action.reviewConfirmations ?? []) {
    const meta = record.fieldMeta[fieldPath];

    if (meta && meta.reviewState !== "confirmed") {
      record.fieldMeta[fieldPath] = {
        ...meta,
        reviewState: "confirmed",
        updatedAt: context.appliedAt,
        updatedBy: context.updatedBy,
      };
    }
  }

  return record;
}
